<template>
  <div
    id="criterionWrapper"
    ref="criterionWrapper"
    class="criterionWrapper"
  >
    <h4 class="mb-4">
      Klauseln
    </h4>

    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />

    <div
      v-if="
        totalNumberOfCriterions != 0 &&
          loadedCriterions != totalNumberOfCriterions
      "
      class="w-100 pb-4"
    >
      <div class="float-right">
        Geladene Klauseln:
        {{ loadedCriterions + " / " + totalNumberOfCriterions }}
      </div>
    </div>

    <u-loader v-if="isLoading" />
    <div v-else>
      <b-tabs
        id="titleTabs"
        v-model="activeTitleIndex"
        class="w-100 mainCriterionTab ml-0 mr-0 pl-0 pr-0"
      >
        <b-tab
          v-for="(CriterionTitle, index) in mainCriterionTitles"
          :key="index"
          v-b-tooltip.hover
          :title="CriterionTitle.TitleName"
          class="mainCriterionTab"
          placement="bottom"
        >
          <template slot="title">
            <div
              class="tabsControlPreventDefault"
              @click="titleClicked(index)"
            >
              {{ CriterionTitle.value }}
            </div>
          </template>
          <b-card class="shadow-sm mainCriterionManagement">
            <h1 class="ml-4 mb-4">
              {{ CriterionTitle.value }}
            </h1>

            <b-tabs
              v-if="index == activeTitleIndex"
              id="subTitleTabs"
              v-model="activeSubTitleIndex"
              class="w-100 subCriterionTab ml-0 mr-0 pl-0 pr-0"
            >
              <b-tab
                v-for="(CriterionSubTitle, subTitleIndex) in getSubTitles(
                  CriterionTitle.path,
                )"
                :key="subTitleIndex"
                :title="CriterionSubTitle.SubTitleName"
                @click="subTitleClicked(subTitleIndex)"
              >
                <template slot="title">
                  <div
                    class="tabsControlPreventDefault"
                    @click="subTitleClicked(subTitleIndex)"
                  >
                    {{ CriterionSubTitle.value }}
                  </div>
                </template>
                <b-card class="shadow-sm subCriterionManagement">
                  <h2 class="mb-4">
                    {{ CriterionSubTitle.value }}
                  </h2>
                  <div
                    v-for="(Criterion, criterionIndex) in getCriterions(
                      CriterionSubTitle.path,
                    )"
                    :key="criterionIndex"
                  >
                    <u-loader v-if="Criterion.isLoading" />
                    <div
                      v-else
                      class="criterionHeaderWrapper"
                    >
                      <h5>{{ Criterion.value }}</h5>
                      <span class="criterionSubTitle">
                        {{ Criterion.isAve ? "[Ave-Relevant]" : "" }}
                        {{
                          Criterion.isTenantGavService
                            ? "[Tenant GavService]"
                            : ""
                        }}
                        {{ Criterion.isTenantUnia ? "[Tenant Unia] " : "" }}
                        {{
                          Criterion.isTenantTempData ? "[Tenant TempData]" : ""
                        }}
                        {{ Criterion.isTenantSeco ? "[Tenant Seco] " : "" }}
                        {{
                          Criterion.useWhenNoPvl
                            ? "[Vertragsloser-Zustand-PVL]"
                            : ""
                        }}
                        {{
                          Criterion.isImportantKlausel
                            ? "[Wichtige Klauseln]"
                            : ""
                        }}
                        &nbsp;
                      </span>

                      <div class="actionsWrapper">
                        <div class="form-check pl-0">
                          <label>
                            <input
                              v-model="Criterion.Data.usePVL"
                              :disabled="
                                !canUserEdit ||
                                  !isActiveCriterion(Criterion.path)
                              "
                              name="PVL: Kern-GAV verwenden"
                              type="checkbox"
                            >
                            <span
                              class="label-text"
                            >PVL: Kern-GAV verwenden</span>
                          </label>
                        </div>
                        <div class="form-check pl-0">
                          <label>
                            <input
                              v-model="Criterion.Data.useGB"
                              :disabled="
                                !canUserEdit ||
                                  !isActiveCriterion(Criterion.path)
                              "
                              name="Gesetzliche Bestimmungen"
                              type="checkbox"
                            >
                            <span
                              class="label-text"
                            >Gesetzliche Bestimmungen</span>
                          </label>
                        </div>
                        <div>
                          <button
                            v-for="lang in languages"
                            :key="lang"
                            :class="{
                              boldLanguageLink: activeLanguage == lang,
                            }"
                            class="btn link-btn languageLinkButton"
                            @click="activeLanguage = lang"
                          >
                            {{ lang.substring(0, 2).toUpperCase() }}
                          </button>
                          <button
                            v-if="languages.length >= 2"
                            v-b-tooltip.hover
                            :class="{
                              boldLanguageLink: activeLanguage == 'all',
                            }"
                            class="btn link-btn languageLinkButton"
                            placement="bottom"
                            title="Alle Sprachen anzeigen"
                            @click="activeLanguage = 'all'"
                          >
                            <i class="fas fa-expand" />
                          </button>
                          <button
                            v-if="
                              activeCriterionPath != Criterion.path &&
                                canUserEdit
                            "
                            v-b-tooltip.hover
                            class="btn btn-primary smallActionButton"
                            placement="bottom"
                            title="Klausel bearbeiten"
                            type="button"
                            @click="
                              setCriterionToEditMode(
                                index,
                                subTitleIndex,
                                Criterion.path,
                              )
                            "
                          >
                            <i class="fas fa-edit" />
                          </button>
                        </div>
                      </div>
                      <div class="checkBoxesWrapper" />
                      <hr>
                      <div class="mt-4 mb-4">
                        <div
                          v-if="isActiveCriterion(Criterion.path)"
                          class="criterionEdit"
                        >
                          <div v-if="activeLanguage != 'all'">
                            <tiny-mce-editor
                              v-for="lang in languages"
                              v-show="activeLanguage === lang"
                              :key="lang"
                              :value="
                                getCriterionValuesByLang(
                                  lang,
                                  Criterion.Data.klauselContents,
                                )
                              "
                              @input="
                                updateCriterionValuesByLang(
                                  $event,
                                  lang,
                                  Criterion.Data.klauselContents,
                                  Criterion.path,
                                )
                              "
                            />
                          </div>
                          <div
                            v-else
                            :style="'left: -' + fullWidthleft + 'px;'"
                            class="row oversized"
                          >
                            <div
                              v-for="lang in languages"
                              :key="lang"
                              class="col-4 languageCriterionWrapper"
                            >
                              <tiny-mce-editor
                                :value="
                                  getCriterionValuesByLang(
                                    lang,
                                    Criterion.Data.klauselContents,
                                  )
                                "
                                @input="
                                  updateCriterionValuesByLang(
                                    $event,
                                    lang,
                                    Criterion.Data.klauselContents,
                                    Criterion.path,
                                  )
                                "
                              />
                            </div>
                          </div>
                          <div
                            v-if="canUserEdit"
                            class="mt-2 mb-2"
                          >
                            <b-button
                              v-b-tooltip.hover
                              :disabled="criterionSaving"
                              name="saveVersionEditionDetails"
                              placement="bottom"
                              title="Alle Sprachen werden gespeichert"
                              type="submit"
                              variant="success"
                              @click="SaveCurrentCriterion(Criterion.path)"
                            >
                              Speichern
                            </b-button>
                            <b-button
                              v-b-tooltip.hover
                              class="ml-2"
                              placement="bottom"
                              title="Alle nicht gespeichert Änderungen dieses Kriteriums gehen verloren"
                              variant="danger"
                              @click="ResetCurrentCriterion(Criterion.path)"
                            >
                              Abbrechen
                            </b-button>
                          </div>
                        </div>
                        <div
                          v-show="!isActiveCriterion(Criterion.path)"
                          class="criterionView position-relative"
                        >
                          <div v-if="activeLanguage != 'all'">
                            <p
                              v-for="lang in languages.filter(
                                (x) => x === activeLanguage,
                              )"
                              :key="lang"
                              v-html="
                                getCriterionValuesByLang(
                                  lang,
                                  Criterion.Data.klauselContents,
                                )
                              "
                            />
                          </div>
                          <div
                            v-else
                            :style="'left: -' + fullWidthleft + 'px;'"
                            class="row oversized"
                          >
                            <div
                              v-for="lang in languages"
                              :key="lang"
                              class="col-4 languageCriterionWrapper"
                            >
                              <p
                                v-html="
                                  getCriterionValuesByLang(
                                    lang,
                                    Criterion.Data.klauselContents,
                                  )
                                "
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card>
              </b-tab>
            </b-tabs>
          </b-card>
        </b-tab>
      </b-tabs>
    </div>
    <b-modal
      id="notSavedModal"
      ref="notSavedModal"
      hide-footer
      no-fade
      title="Ungespeicherte Änderungen"
    >
      <p>Sie haben noch ungespeicherte Daten</p>
      <p>Wollen Sie die Änderungen speichern oder verwerfen?</p>
      <button
        class="btn btn-success mr-2"
        @click="SaveAndNavigate"
      >
        Änderungen Speichern
      </button>
      <button
        class="btn btn-danger mr-2"
        @click="CancelAndNavigate"
      >
        Änderungen verwerfen
      </button>
      <button
        class="btn btn-secondary"
        @click="Cancel"
      >
        Abbrechen
      </button>
    </b-modal>
  </div>
</template>

<script>
import { MultiLangMixin } from "@/mixins/MultilanguageContentsMixin";
import {
  GetCriterion,
  SaveCriterion,
} from "@/services/contract.edition.criterion.service.js";
import { LoadAllCriterionConfigurations } from "@/services/criterion.management.service.js";
import TinyMceEditor from "@/components/common/tinyMceEditor.vue";

export default {
  components: {
    TinyMceEditor,
  },
  mixins: [MultiLangMixin],
  props: {
    contractId: {
      type: String,
      default: "",
    },
    versionId: {
      type: Number,
      default: 0,
    },
    editionId: {
      type: Number,
      default: 0,
    },
    canUserEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      criterionSaving: false,
      isLoading: false,
      errorObject: null,
      activeTitleIndex: 0,
      activeSubTitleIndex: 0,
      activeCriterionPath: "",
      newActiveCriterionPath: "",
      navigateToCriterion: [0, 0],
      activeLanguage: "de-CH",
      allCriterionConfiguration: null,
      totalNumberOfCriterions: 0,
      loadedCriterions: 0,
      loadingProgress: 0,
      languages: [],
      fullWidthleft: 0,
      currentOriginalCriterionObject: { Data: {} },
    };
  },
  computed: {
    mainCriterionTitles() {
      return this.allCriterionConfiguration
        ? this.allCriterionConfiguration.filter(
            (config) => config.path.match(/\//g).length == 1,
          )
        : [];
    },
  },
  watch: {
    activeTitleIndex() {
      this.activeSubTitleIndex = 0;
    },
    activeLanguage() {
      if (this.activeCriterionPath) {
        this.loadEditorContent(this.activeCriterionPath);
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  created() {
    this.initializeComponent();
  },
  methods: {
    initializeComponent() {
      this.isLoading = true;
      this.errorObject = null;

      if (this.$store.state.adminCurrentContract.ContractLanguage) {
        this.activeLanguage =
          this.$store.state.adminCurrentContract.ContractLanguage;
      }

      if (this.$store.state.adminCurrentContract.TranslatedLanguages) {
        this.languages =
          this.$store.state.adminCurrentContract.TranslatedLanguages.map(
            (lang) => lang.language,
          );
        if (!this.languages.includes(this.activeLanguage)) {
          this.activeLanguage = this.languages[0];
        }
      }

      this.loadAllCriterionConfigurations();
    },
    loadAllCriterionConfigurations() {
      LoadAllCriterionConfigurations(
        (response) => {
          this.allCriterionConfiguration = response.map((config) => ({
            ...config,
            isLoading: true,
          }));
          this.isLoading = false;
          this.totalNumberOfCriterions = this.allCriterionConfiguration.filter(
            (config) => !config.isRootGroup,
          ).length;
          this.loadAllCriterions();
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
        },
        false,
      );
    },
    loadAllCriterions() {
      const localKlauselContents = this.languages.map((language) => ({
        id: 0,
        klauselId: 0,
        value: "",
        language,
      }));

      this.allCriterionConfiguration.forEach((configuration) => {
        if (!configuration.isRootGroup) {
          GetCriterion(
            this.contractId,
            this.versionId,
            this.editionId,
            configuration.klauselTypeId,
            (response) => {
              response.klauselContents = response.klauselContents.length
                ? response.klauselContents
                : JSON.parse(JSON.stringify(localKlauselContents));
              const config = this.allCriterionConfiguration.find(
                (criterionConfiguration) =>
                  criterionConfiguration.klauselTypeId ===
                  configuration.klauselTypeId,
              );
              config.Data = response;
              config.isLoading = false;
              this.loadedCriterions++;
              this.loadingProgress =
                (100 / this.totalNumberOfCriterions) * this.loadedCriterions;
            },
            (error) => {
              if (error.response.status === 404) {
                const config = this.allCriterionConfiguration.find(
                  (criterionConfiguration) =>
                    criterionConfiguration.klauselTypeId ===
                    configuration.klauselTypeId,
                );
                config.Data = {
                  id: 0,
                  Code: "newCriterion",
                  usePVL: false,
                  useGB: false,
                  editionId: this.editionId,
                  klauselTypId: configuration.klauselTypeId,
                  klauselContents: JSON.parse(
                    JSON.stringify(localKlauselContents),
                  ),
                };
                config.isLoading = false;
                this.loadedCriterions++;
                this.loadingProgress =
                  (100 / this.totalNumberOfCriterions) * this.loadedCriterions;
              }
            },
          );
        }
      });
    },
    getSubTitles(path) {
      return this.allCriterionConfiguration.filter(
        (config) =>
          config.path.match(/\//g).length == 2 &&
          config.path.indexOf(path) == 0,
      );
    },
    getCriterions(path) {
      return this.allCriterionConfiguration.filter(
        (config) => !config.isRootGroup && config.path.indexOf(path) == 0,
      );
    },
    getCriterionValuesByLang(language, klauselContents) {
      const content = klauselContents.find(
        (content) => content.language === language,
      );
      return content ? content.value : "";
    },
    updateCriterionValuesByLang(event, language, klauselContents, klauselPath) {
      const content = klauselContents.find(
        (content) => content.language === language,
      );
      if (content && klauselPath && this.isActiveCriterion(klauselPath)) {
        content.value = event;
      }
    },
    SaveAndNavigate() {
      this.SaveCurrentCriterion(this.activeCriterionPath);
      this.$refs["notSavedModal"].hide();
      this.activeTitleIndex = this.navigateToCriterion[0];
      this.activeSubTitleIndex = this.navigateToCriterion[1];
    },
    CancelAndNavigate() {
      this.ResetCurrentCriterion(this.activeCriterionPath);
      this.$refs["notSavedModal"].hide();
      this.activeCriterionPath = this.newActiveCriterionPath;
      setTimeout(() => {
        this.activeTitleIndex = this.navigateToCriterion[0];
        this.activeSubTitleIndex = this.navigateToCriterion[1];
      }, 10);
    },
    Cancel() {
      this.$refs["notSavedModal"].hide();
    },
    titleClicked(titleId) {
      if (!this.activeCriterionPath || this.areChangesSaved()) {
        this.activeTitleIndex = titleId;
        this.activeCriterionPath = "";
      } else {
        this.navigateToCriterion = [titleId, this.activeSubTitleIndex];
        this.newActiveCriterionPath = "";
        this.$refs["notSavedModal"].show();
      }
    },
    subTitleClicked(subTitleId) {
      if (!this.activeCriterionPath || this.areChangesSaved()) {
        this.activeSubTitleIndex = subTitleId;
        this.activeCriterionPath = "";
      } else {
        this.navigateToCriterion = [this.activeTitleIndex, subTitleId];
        this.newActiveCriterionPath = "";
        this.$refs["notSavedModal"].show();
      }
    },
    setCriterionToEditMode(titleIndex, subTitleIndex, newActiveCriterionPath) {
      if (!this.activeCriterionPath || this.areChangesSaved()) {
        this.activeTitleIndex = titleIndex;
        this.activeSubTitleIndex = subTitleIndex;
        this.activeCriterionPath = newActiveCriterionPath;
        this.currentOriginalCriterionObject = JSON.parse(
          JSON.stringify(
            this.allCriterionConfiguration.find(
              (config) => config.path === newActiveCriterionPath,
            ),
          ),
        );
      } else {
        this.navigateToCriterion = [titleIndex, subTitleIndex];
        this.newActiveCriterionPath = newActiveCriterionPath;
        this.$refs["notSavedModal"].show();
      }
    },
    isActiveCriterion(activeCriterionPath) {
      return this.activeCriterionPath === activeCriterionPath;
    },
    SaveCurrentCriterion(criterionPath) {
      const currentCriterion = this.allCriterionConfiguration.find(
        (config) => config.path === criterionPath,
      );
      if (currentCriterion) {
        currentCriterion.isLoading = true;
        this.criterionSaving = true;
        SaveCriterion(
          this.contractId,
          this.versionId,
          this.editionId,
          currentCriterion.Data.klauselTypId,
          currentCriterion.Data,
          () => {
            GetCriterion(
              this.contractId,
              this.versionId,
              this.editionId,
              currentCriterion.Data.klauselTypId,
              (response) => {
                this.criterionSaving = false;
                Object.assign(currentCriterion, {
                  Data: response,
                  isLoading: false,
                });
                this.activeCriterionPath = this.newActiveCriterionPath || "";
              },
              (error) => {
                this.criterionSaving = false;
                this.errorObject = error;
                currentCriterion.isLoading = false;
              },
            );
          },
          (error) => {
            this.criterionSaving = false;
            this.errorObject = error;
            currentCriterion.isLoading = false;
          },
        );
      }
    },
    ResetCurrentCriterion(criterionPath) {
      const currentCriterion = this.allCriterionConfiguration.find(
        (config) => config.path === criterionPath,
      );
      if (currentCriterion) {
        currentCriterion.isLoading = true;
        Object.assign(
          currentCriterion.Data,
          JSON.parse(JSON.stringify(this.currentOriginalCriterionObject.Data)),
        );
        this.activeCriterionPath = "";

        setTimeout(() => {
          currentCriterion.isLoading = false;
        }, 10);
      }
    },
    handleResize() {
      this.fullWidthleft =
        (window.innerWidth - this.$refs["criterionWrapper"].clientWidth) / 2;
    },
    areChangesSaved() {
      const currentCriterionObject = this.allCriterionConfiguration.find(
        (criterion) => criterion.path === this.activeCriterionPath,
      );
      return (
        JSON.stringify(currentCriterionObject) ===
        JSON.stringify(this.currentOriginalCriterionObject)
      );
    },
  },
};
</script>

<style>
.criterionWrapper .tabsControlPreventDefault {
  padding: 0.5rem 1rem;
  margin: -1px;
}

.criterionWrapper .nav-link {
  padding: 0px;
}

.criterionWrapper .table th,
.criterionWrapper .table td {
  border: 1px solid black;
  padding: 6px;
}

.criterionWrapper table th,
.criterionWrapper table td {
  border: 1px solid black;
  padding: 6px;
}

.criterionWrapper .subCriterionTab li:first-child[role="presentation"] {
  margin-left: 20px;
}

.criterionWrapper .nonave {
  background-color: rgba(41, 205, 255, 0.432);
}

.criterionWrapper .smartcom {
  background-color: rgba(255, 0, 14, 0.327);
}

.criterionWrapper table {
  background-color: inherit;
}

.criterionWrapper .nonpvl {
  background-color: rgba(248, 248, 0, 0.377);
}

.criterionWrapper table {
  width: 100% !important;
}

.languageLinkButton {
  padding: 4px;
  color: red;
  font-size: 16px;
}

.boldLanguageLink {
  font-weight: bold;
}

.mainCriterionTab {
  font-size: 16px;
}

.mainCriterionManagement {
  border-top: none;
}

.subCriterionTab {
  font-size: 13px;
}

.subCriterionManagement {
  border-top: none;
  padding: 10px 30px;
}

.criterionHeaderWrapper {
  margin-bottom: 30px;
  padding-bottom: 5px;
}

.criterionSubTitle {
  font-style: italic;
  padding-top: 5px;
}

.actionsWrapper {
  float: right;
  display: flex;
}

.form-check {
  padding-top: 5px;
  padding-right: 15px;
}

.languageCriterionWrapper {
  overflow-x: auto;
  border: 1px solid #bbb;
  padding: 22px;
}

.oversized {
  width: calc(100vw - 30px);
  position: relative;
  background-color: white;
}
</style>
